import { Buffer } from 'buffer';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Reports } from '@pncl/common-models';
import { AnalyticsReportRawUser } from '../reports/analytics-reports-models';
import {
  AttendanceAnalyticsRawUser,
  AttendanceReportRawEvent,
} from '../reports/attendance/attendance-reports-models';
import { ReportsDateRange } from '../reports/reports-models';
import {
  FilterData,
  IFilterAPI,
} from '../standalones/components/generic-filters-view/filters.interface';
import { BillingReport } from '../models/payment';
import { BillingSessionDetails } from '../settings/user-settings-panel/user-settings-billable-usage/billing-usage-utils';
import { InstitutionRecordingsReport } from '../models/space-recording';
import { headersWithSkipErrorInterceptor } from '../sessions/session/request-access/base/request-access-client-base.service';
import { SiteAnalyticsResponse } from '../reports/sites/site-attendance-reports.models';
import { InstitutionTeachersReportResponse } from '../reports/teachers/teachers-report-models';
import { URLService } from './dynamic-url.service';
import { changeFiltersToAPIFormat } from './filters.service';

/** Everything but the teacher and the date range on the coaching sessions request. */
export interface TeacherCoachingSessionsQuery {
  filters?: FilterData[];
  pageNumber?: number;
  pageSize?: number;
  /** Adds the full-window score points the dashboards chart; off for page flips. */
  includeScoresOverTime?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(private http: HttpClient, private urlService: URLService) {}
  private encodeFilters(reportsDateRange: ReportsDateRange, filters?: IFilterAPI) {
    const allFilters = { ...reportsDateRange, ...(filters ? filters : {}) };
    return Buffer.from(encodeURIComponent(JSON.stringify(allFilters))).toString('base64');
  }
  getInstitutionAnalyticsReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
  ): Observable<AnalyticsReportRawUser[]> {
    return this.http.get<AnalyticsReportRawUser[]>(
      `${this.urlService.getDynamicUrl()}/reports/analytics/?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}`,
    );
  }

  getInstitutionTeachersReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
    pageNumber: number = 1,
    pageSize: number = 10,
    includeSummary: boolean = true,
    includeScoresOverTime: boolean = false,
  ): Observable<InstitutionTeachersReportResponse> {
    return this.http.get<InstitutionTeachersReportResponse>(
      `${this.urlService.getDynamicUrl()}/reports/teachers?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}&pageNumber=${pageNumber}&pageSize=${pageSize}&includeSummary=${includeSummary}&includeScoresOverTime=${includeScoresOverTime}`,
      headersWithSkipErrorInterceptor,
    );
  }

  getInstitutionAttendanceReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
  ): Observable<AttendanceAnalyticsRawUser[]> {
    return this.http.get<AttendanceAnalyticsRawUser[]>(
      `${this.urlService.getDynamicUrl()}/reports/attendance/?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}`,
    );
  }

  getInstSiteAnalyticsReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
  ): Observable<SiteAnalyticsResponse> {
    return this.http.get<SiteAnalyticsResponse>(
      `${this.urlService.getDynamicUrl()}/reports/siteAnalytics/?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}`,
    );
  }
  getSiteCoachingAnalyticsReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
  ): Observable<Reports.SiteCoachingAnalyticsResponse> {
    return this.http.get<Reports.SiteCoachingAnalyticsResponse>(
      `${this.urlService.getDynamicUrl()}/reports/siteCoachingAnalytics/?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}`,
    );
  }

  getTeacherCoachingSessions(
    userId: string,
    reportsDateRange: ReportsDateRange,
    {
      filters,
      pageNumber = 1,
      pageSize = 10,
      includeScoresOverTime = false,
    }: TeacherCoachingSessionsQuery = {},
  ): Observable<Reports.TeacherCoachingSessionsResponse> {
    const params = new HttpParams({
      fromObject: {
        filters: this.encodeFilters(reportsDateRange, changeFiltersToAPIFormat(filters)),
        pageNumber,
        pageSize,
        includeScoresOverTime,
      },
    });

    return this.http.get<Reports.TeacherCoachingSessionsResponse>(
      `${this.urlService.getDynamicUrl()}/reports/coaching/teachers/${userId}/sessions`,
      { params },
    );
  }

  getInstitutionRecordingseReport(
    reportsDateRange: ReportsDateRange,
    filters?: FilterData[],
    pageNumber: number = 1,
    pageSize: number = 10,
  ): Observable<InstitutionRecordingsReport> {
    return this.http.get<InstitutionRecordingsReport>(
      `${this.urlService.getDynamicUrl()}/reports/recordings?filters=${this.encodeFilters(
        reportsDateRange,
        changeFiltersToAPIFormat(filters),
      )}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
      headersWithSkipErrorInterceptor,
    );
  }

  getUserAttendanceReport(
    userId: string,
    reportsDateRange: ReportsDateRange,
  ): Observable<AttendanceReportRawEvent[]> {
    return this.http.get<AttendanceReportRawEvent[]>(
      `${this.urlService.getDynamicUrl()}/reports/attendance/${userId}/?filters=${this.encodeFilters(
        reportsDateRange,
      )}`,
    );
  }

  getBillingUsageReport(): Observable<BillingReport> {
    return this.http.get<BillingReport>(`${this.urlService.getDynamicUrl()}/reports/billingUsage`);
  }

  getBillableSessionReport(
    reportsDateRange: ReportsDateRange,
  ): Observable<BillingSessionDetails[]> {
    return this.http.get<BillingSessionDetails[]>(
      `${this.urlService.getDynamicUrl()}/reports/billingSessions?filters=${this.encodeFilters(
        reportsDateRange,
      )}`,
    );
  }
}
